import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../../backend/firebaseConfig";
import { fetchLogoUrl } from "./FetchLogo";

const AdviserListContext = createContext({
  advisers: [],
  loadingAdvisers: true,
});

function buildAdviserDisplayName(data = {}, docId = "") {
  const firstName = data?.preferredName || data?.firstName || data?.forename || "";
  const preferredName = data?.preferredName || "";
  const lastName = data?.lastName || data?.surname || "";

  const personName = [preferredName || firstName, lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    personName ||
    data?.tradingName ||
    data?.tradingStyle ||
    data?.brandName ||
    data?.email ||
    docId
  );
}


export const AdviserListProvider = ({ children }) => {
  const [advisers, setAdvisers] = useState([]);
  const [loadingAdvisers, setLoadingAdvisers] = useState(true);

  useEffect(() => {
    const ref = collection(db, "Authorized Members", "Advisers", "Data");

    // Helper to fetch Personal Details subcollection for a docSnap
    const fetchPersonalDetails = async (docSnap) => {
      try {
        const personalRef = collection(
          db,
          "Authorized Members",
          "Advisers",
          "Data",
          docSnap.id,
          "Personal Details",
        );
        const personalSnap = await import("firebase/firestore").then((m) =>
          m.getDocs(personalRef),
        );
        const personal = personalSnap.docs.find((d) => d.id === "Data");
        return personal ? personal.data() : {};
      } catch {
        return {};
      }
    };

    let unsub = null;
    let cancelled = false;

    unsub = onSnapshot(
      ref,
      async (snap) => {
        // Fetch all personal details in parallel
        const adviserPromises = snap.docs.map(async (docSnap) => {
          const data = docSnap.data() || {};
          if (data?.isAppointed === false) return null;

          // Fetch personal details
          const personal = await fetchPersonalDetails(docSnap);
          const email = data?.email || personal?.emailAddress || docSnap.id || "";
          const brandName =
            data?.brandName ||
            personal?.brandName ||
            data?.tradingName ||
            personal?.tradingName ||
            data?.tradingStyle ||
            personal?.tradingStyle ||
            "";
          const logoUrl =
            data?.logoUrl ||
            personal?.logoUrl ||
            (await fetchLogoUrl(brandName));

          return {
            id: email,
            value: email,
            email,
            adviserId: docSnap.id,
            name: buildAdviserDisplayName({ ...data, ...personal }, docSnap.id),
            firstName:
              personal?.forename ||
              personal?.firstName ||
              data?.firstName ||
              data?.forename ||
              "",
            lastName:
              personal?.surname ||
              personal?.lastName ||
              data?.lastName ||
              data?.surname ||
              "",
            preferredName: personal?.preferredName || data?.preferredName || "",
            dob:
              personal?.dob ||
              personal?.dateOfBirth ||
              personal?.birthDate ||
              data?.dob ||
              data?.dateOfBirth ||
              data?.birthDate ||
              null,
            tradingName: data?.tradingName || "",
            tradingStyle: data?.tradingStyle || "",
            brandName,
            logoUrl,
            profileUrl: data?.profileUrl || personal?.profileUrl || "",
            isAR: !!data?.isAR,
          };
        });
        const next = (await Promise.all(adviserPromises)).filter(Boolean);
        if (!cancelled) {
          setAdvisers(next);
          setLoadingAdvisers(false);
        }
      },
      (error) => {
        console.error("Failed to load adviser list:", error);
        setAdvisers([]);
        setLoadingAdvisers(false);
      },
    );

    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, []);

  const value = useMemo(
    () => ({ advisers, loadingAdvisers }),
    [advisers, loadingAdvisers],
  );

  return (
    <AdviserListContext.Provider value={value}>
      {children}
    </AdviserListContext.Provider>
  );
};

export const useAdviserList = () => useContext(AdviserListContext);
